import { t } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { cn } from '@/lib/utils';
import { Button } from '@/ui/components/arandu/button';
import { IconButton } from '@/ui/components/arandu/icon-button';
import { useEmblaCarousel, usePrevNextButtons } from '@/ui/components/carousel';
import { Icon, Icons } from '@/ui/components/icon';

export const SellTickets = () => {
  const { t } = useTranslation();
  const nav = useNavigate();

  return (
    <section className='w-full py-xl flex flex-col gap-xl pl-mini bg-gradient-to-b from-[#f1f0ff] to-[#d0ceff]'>
      <div className='w-full flex flex-col gap-xs md:gap-xl text-center'>
        <header className='w-full max-w-[75rem] mx-auto flex flex-col gap-xs md:gap-xl pr-mini'>
          <h2 className='ts-heading-lg md:ts-heading-xxxl text-base-primary-600 text-center'>
            <Trans
              components={{
                span: <span className='text-base-primary-500 block' />,
              }}
              i18nKey='home.sell-tickets.title'
            />
          </h2>

          <p className='ts-subtitle-sm text-ctx-content-base'>{t('home.sell-tickets.description')}</p>
        </header>

        <SellTicketsCarrousel />

        <p className='ts-paragraph-sm text-ctx-content-base pr-mini'>{t('home.sell-tickets.contact-us')}</p>

        <Button className='mx-auto !duration-500 ease-in-out' onClick={() => nav('/contact')} type='button' size='lg'>
          {t('home.page-contact.title')}
        </Button>
      </div>
    </section>
  );
};

const SellTicketsCarrousel = () => {
  const { t } = useTranslation();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
  });

  const { onPrevButtonClick, onNextButtonClick, prevBtnDisabled, nextBtnDisabled } = usePrevNextButtons([
    emblaRef,
    emblaApi,
  ]);

  return (
    <div className='embla w-full flex flex-col gap-xs overflow-hidden -mt-mini md:mt-0'>
      <div className='w-full flex flex-col max-w-[66rem] mx-auto gap-sm md:flex-row md:justify-between md:items-center md:hidden'>
        <div className='w-full flex gap-mini justify-end pr-mini'>
          <IconButton
            hierarchy='secondary'
            className='embla__prev !duration-500 ease-in-out'
            ariaLabel={t('components.pagination.previous')}
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
            icon={() => <Icon className='text-ctx-content-title' icon='arrow-left' />}
          />
          <IconButton
            hierarchy='secondary'
            className='embla__next !duration-500 ease-in-out'
            ariaLabel={t('components.pagination.next')}
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
            icon={() => <Icon className='text-ctx-content-title' icon='arrow-right' />}
          />
        </div>
      </div>

      <div className='flex max-w-[67rem] md:mx-auto'>
        <div className='embla__viewport overflow-hidden' ref={emblaRef}>
          <div className='embla__container flex gap-xxs lg:gap-xl'>
            {items.map((item, index) => (
              <div className='group' key={item.title}>
                <div
                  className={cn(
                    'embla__slide w-[calc(100vw-2rem)] max-w-[19.125rem] md:min-w-[19.125rem] h-[18.75rem] flex flex-col gap-xxs rounded-md pt-[9rem] px-9 transition-all duration-300 relative overflow-hidden',
                    {
                      'mr-mini md:mr-0': index === items.length - 1,
                    },
                  )}
                >
                  <img
                    className='absolute top-0 left-0 w-full h-full object-cover'
                    src={item.src}
                    alt={item.description}
                  />

                  <div className='absolute inset-0 bg-base-neutral-black/transparent bg-gradient-to-b from-base-neutral-black/transparent to-base-neutral-black/opaque transition-colors duration-300 ease-out group-hover:from-base-neutral-black/intense group-hover:to-base-neutral-black/opaque group-hover:bg-base-neutral-black/deep' />

                  <div className='flex flex-col items-start gap-mini text-left'>
                    <div className='bg-base-primary-500 size-sm rounded-pill flex items-center justify-center transform transition-all duration-500 group-hover:-translate-y-[4rem]'>
                      <Icon className='size-[1.171rem] text-base-neutral-white' icon={item.icon} />
                    </div>
                    <h3
                      className={cn(
                        'ts-heading-md text-base-neutral-white transform transition-all relative duration-500 group-hover:-translate-y-[4rem]',
                        {
                          'mb-xxs group-hover:mb-0': item.title.length < 16,
                        },
                      )}
                    >
                      {item.title}
                    </h3>
                    <p className='ts-paragraph-xs opacity-transparent group-hover:opacity-opaque text-base-neutral-white relative -top-16 transition-all duration-500 ease-in-out'>
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const items: {
  icon: Icons;
  title: string;
  description: string;
  src: string;
}[] = [
  {
    icon: 'gift',
    title: t('home.sell-tickets.free-events'),
    description: t('home.sell-tickets.promote-for-free'),
    src: 'webp/sell-tickets/st1.webp',
  },
  {
    icon: 'ticket',
    title: t('home.sell-tickets.ticket-fee'),
    description: t('home.sell-tickets.transparent-pricing'),
    src: 'webp/sell-tickets/st2.webp',
  },
  {
    icon: 'thumbs-up',
    title: t('home.sell-tickets.special-conditions'),
    description: t('home.sell-tickets.benefits'),
    src: 'webp/sell-tickets/st3.webp',
  },
];
